import {
  ModBanner,
  ModButton,
  ModIconButton,
  ModSkeleton,
  ModTooltip
} from '@moderneinc/design-system-components';
import {
  borderRadius,
  breakpoints,
  iconSize,
  semanticColors,
  spacing,
  typography
} from '@moderneinc/design-system-tokens';
import {
  Dialog,
  Divider,
  dialogClasses,
  InputBase,
  Stack,
  Typography
} from '@mui/material';
import {
  bindDialog,
  bindTrigger,
  usePopupState
} from 'material-ui-popup-state/hooks';
import React, {
  type FunctionComponent,
  type KeyboardEventHandler,
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import {
  ORG_SEARCH_INPUT_ID,
  SEARCH_OVERLAY_TOP_SPACING
} from '../../../constants/general';
import { navFieldSx } from '../../../constants/sx';
import {
  GLOBAL_ORGANIZATION_DISPLAY_NAME,
  GLOBAL_ORGANIZATION_ID,
  getOrganizationDisplayName,
  MISSING_ORGANIZATION_DISPLAY_NAME
} from '../../../helpers/organizations.helper';
import {
  orgSelectorClosedEvent,
  orgSelectorEvent,
  orgSelectorMissingOrgClickEvent
} from '../../../hooks/use-app-event.hooks';
import { useMenuTriggerTooltip } from '../../../hooks/use-menu-trigger-tooltip.hook';
import { useSelectedOrganization } from '../../../hooks/use-organizations.hooks';
import {
  CloseIcon,
  CollapseIcon,
  ErrorIcon,
  GlobalOrganizationIcon,
  OrganizationIcon,
  SearchIcon
} from '../../../icons/icons';
import { useSelectedOrganizationStore } from '../../../stores/organization-store';
import {
  selectActiveStepId,
  useTourEngineStore
} from '../../../stores/tour-engine.store';
import {
  OrganizationsTable,
  type OrganizationsTableRef
} from '../../organizations/organizations.table.component';
import { MiddleTruncatedLabel } from '../../utilities/middle-truncated-label/middle-truncated-label.component';

export const GlobalOrganizationSelector: FunctionComponent = () => {
  const { selectedOrganization, isMissingOrg, loading } =
    useSelectedOrganization();
  const popupState = usePopupState({
    variant: 'dialog',
    popupId: 'global-organization-selector'
  });
  const tooltipProps = useMenuTriggerTooltip(popupState.isOpen);

  const organizationName = isMissingOrg
    ? MISSING_ORGANIZATION_DISPLAY_NAME
    : getOrganizationDisplayName(selectedOrganization) ||
      GLOBAL_ORGANIZATION_DISPLAY_NAME;
  const isGlobal =
    !isMissingOrg &&
    (selectedOrganization?.id === GLOBAL_ORGANIZATION_ID ||
      !selectedOrganization);
  // Sized per variant rather than a shared wrapper: the lucide org icons take
  // `size`, while the MUI error icon takes `fontSize` — both pinned to the
  // prototype's 18px.
  const selectorIcon = isMissingOrg ? (
    <ErrorIcon sx={{ fontSize: `${iconSize.lg}px` }} />
  ) : isGlobal ? (
    <GlobalOrganizationIcon size={iconSize.lg} />
  ) : (
    <OrganizationIcon size={iconSize.lg} />
  );

  const activeTourStepId = useTourEngineStore(selectActiveStepId);
  const advanceTour = useTourEngineStore((state) => state.advance);
  const isOpenSelectorTourStep = activeTourStepId === 'open-org-selector';
  const isChooseOrgTourStep = activeTourStepId === 'choose-org';

  // The "open-org-selector" tour step advances once the dialog is open.
  useEffect(() => {
    if (isOpenSelectorTourStep && popupState.isOpen) {
      advanceTour();
    }
  }, [isOpenSelectorTourStep, advanceTour, popupState.isOpen]);

  const methods = useForm<{ query: string }>();
  const searchTerm = methods.watch('query') ?? '';
  const tableRef = useRef<OrganizationsTableRef>(null);
  const [showGlobalOrgTourHint, setShowGlobalOrgTourHint] = useState(false);

  const { ref: queryInputRef, ...queryRegisterProps } =
    methods.register('query');

  const resetState = useCallback(() => {
    methods.resetField('query');
    setShowGlobalOrgTourHint(false);
    popupState.close();
    orgSelectorClosedEvent.dispatch();
  }, [methods, popupState]);

  // The "choose-org" tour step advances only on a real org selection. The table
  // calls onClose right after updating the selected org (read fresh from the
  // store, since this render's value is still stale here), whereas Escape / the
  // X button close via resetState directly — so a bare dismiss no longer skips
  // the step. The global org is excluded (its Dry run is disabled, which would
  // soft-lock the next step) — the dialog stays open with a hint instead of
  // closing, since closing would orphan the tour's "choose-org" target.
  const handleTableClose = useCallback(() => {
    if (isChooseOrgTourStep) {
      const selectedId =
        useSelectedOrganizationStore.getState().selectedOrganizationId;
      if (selectedId === GLOBAL_ORGANIZATION_ID) {
        setShowGlobalOrgTourHint(true);
        return;
      }
      advanceTour();
    }
    resetState();
  }, [isChooseOrgTourStep, advanceTour, resetState]);

  const handleButtonClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      if (isMissingOrg) {
        orgSelectorMissingOrgClickEvent.dispatch();
      } else {
        popupState.open(e);
      }
    },
    [isMissingOrg, popupState]
  );

  // Allow other components to open the org selector
  orgSelectorEvent.useListener(popupState.open);

  const handleClearSearch = useCallback(() => {
    methods.resetField('query');
    document.getElementById(ORG_SEARCH_INPUT_ID)?.focus();
  }, [methods]);

  const handleSearchKeyDown: KeyboardEventHandler<HTMLInputElement> =
    useCallback(
      (event) => {
        if (event.key === 'Escape') {
          event.preventDefault();
          event.stopPropagation();
          resetState();
          return;
        }
        tableRef.current?.handleSearchKeyDown(event);
      },
      [resetState]
    );

  return (
    <React.Fragment>
      <Stack
        sx={{
          flex: '0 1 auto',
          minWidth: 0,
          maxWidth: {
            md: '50vw',
            lg: `min(65vw, ${breakpoints.lg}px)`
          }
        }}
      >
        {loading && !selectedOrganization ? (
          <ModSkeleton
            width="100%"
            height={`${spacing.spacing_5}px`}
            sx={{ borderRadius: `${borderRadius.input}px` }}
          />
        ) : (
          <ModTooltip title={organizationName} {...tooltipProps}>
            <ModButton
              variant="tertiary"
              {...(!isMissingOrg ? bindTrigger(popupState) : {})}
              onClick={handleButtonClick}
              disableRipple
              data-testid="org-selector-button"
              data-tour="open-org-selector"
              aria-haspopup="dialog"
              sx={{
                ...navFieldSx,
                textTransform: 'none',
                color: 'text.primary',
                width: '100%',
                minWidth: 0,
                overflow: 'hidden',
                // Error state is the only thing layered on the shared field
                // style; the default border/colour come from navFieldSx.
                ...(isMissingOrg && {
                  color: semanticColors.color.status.error.fg,
                  border: `1px solid ${semanticColors.color.status.error.tint}`,
                  '&:hover': {
                    backgroundColor: semanticColors.input.bgHover,
                    borderColor: semanticColors.color.status.error.solid
                  }
                })
              }}
              startIcon={selectorIcon}
              endIcon={<CollapseIcon size={iconSize.xl} />}
            >
              <Typography
                variant="bodySm"
                component="div"
                sx={{
                  flex: 1,
                  minWidth: 0,
                  fontWeight: typography.fontWeight.medium
                }}
              >
                <MiddleTruncatedLabel label={organizationName} maxLines={1} />
              </Typography>
            </ModButton>
          </ModTooltip>
        )}
      </Stack>
      {/* Raw Dialog instead of ModModal: this search overlay has a non-standard
          layout (search header + scrollable tree + footer) that doesn't fit
          ModModal's header/content structure. */}
      <Dialog
        {...bindDialog(popupState)}
        onClose={resetState}
        data-testid="org-selector-dialog"
        aria-label="Organization selector"
        maxWidth={false}
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor: semanticColors.color.surface.scrim
            }
          }
        }}
        sx={{
          [`& .${dialogClasses.paper}`]: {
            position: 'absolute',
            top: (theme) => theme.spacing(SEARCH_OVERLAY_TOP_SPACING),
            width: '100%',
            maxWidth: 800,
            height: '80vh',
            maxHeight: 600,
            mx: 2,
            borderRadius: `${borderRadius.card}px`,
            border: `1px solid ${semanticColors.color.border.primary}`,
            overflow: 'hidden'
          }
        }}
      >
        <FormProvider {...methods}>
          <Stack data-tour="choose-org" sx={{ height: '100%' }}>
            {/* Search header */}
            <Stack
              direction="row"
              sx={{
                gap: 1.5,
                alignItems: 'center',
                px: 2.5,
                py: 2
              }}
            >
              <SearchIcon
                size={iconSize.md}
                style={{
                  color: semanticColors.color.icon.utility,
                  flexShrink: 0
                }}
              />
              <InputBase
                {...queryRegisterProps}
                inputRef={queryInputRef}
                id={ORG_SEARCH_INPUT_ID}
                onKeyDown={handleSearchKeyDown}
                placeholder="Search organizations..."
                autoFocus
                fullWidth
                sx={{
                  fontSize: '1rem',
                  '& input::placeholder': {
                    color: semanticColors.color.text.inputPlaceholder,
                    opacity: 1,
                    WebkitTextFillColor:
                      semanticColors.color.text.inputPlaceholder
                  }
                }}
              />
              {searchTerm && (
                <ModButton
                  variant="tertiary"
                  size="small"
                  onClick={handleClearSearch}
                  sx={{ flexShrink: 0 }}
                >
                  Clear
                </ModButton>
              )}
              <ModIconButton
                size="small"
                onClick={resetState}
                aria-label="Close organization selector"
                sx={{ flexShrink: 0 }}
              >
                <CloseIcon size={'1.25rem'} />
              </ModIconButton>
            </Stack>

            <Divider />

            {showGlobalOrgTourHint && (
              <ModBanner
                data-testid="org-selector-global-org-tour-hint"
                intent="warning"
                type="outlined"
                message="Select a non-Global organization to continue the tour."
                messagePosition="left"
              />
            )}

            {/* Tree content + footer */}
            <OrganizationsTable ref={tableRef} onClose={handleTableClose} />
          </Stack>
        </FormProvider>
      </Dialog>
    </React.Fragment>
  );
};
